import { chronicleLiveOfflineAtom, chronicleSessionIdAtom } from '@/atoms/chronicle';
import { useIsReleaseRemoteSyncAsyncWebEnabled } from '@/features/flags';
import { useAtomValue } from 'jotai';

/**
 * True when `sessionId` is the session held live-offline by the Remote device
 * (BLE dropped mid-recording with remote sync/async enabled, APP-7866).
 *
 * Matches against EITHER the hold's bound session id (`liveOffline.sessionId`,
 * sourced from the device `recordingInfo.sessionId`) OR the active Chronicle
 * session id (`chronicleSessionIdAtom`, the backend id the session list, route
 * and interrupted-set are keyed on). these two can be different id
 * representations of the same held recording, so a plain `??` fallback would
 * short-circuit on a non-null bound id and miss the row keyed by the backend
 * id — the session-list cell then hid the offline icon while the header badge
 * (keyed on the route/backend id) showed. Matching either id keeps the header
 * badge, the note-canvas overlay and the list row in lockstep. During a hold
 * there is exactly one held recording, so at most that one session matches —
 * no false positives.
 */
export function useIsLiveOfflineRemoteSession(
  sessionId: string | null | undefined
): boolean {
  const isRemoteSyncAsyncEnabled = useIsReleaseRemoteSyncAsyncWebEnabled();
  const liveOffline = useAtomValue(chronicleLiveOfflineAtom);
  const activeChronicleSessionId = useAtomValue(chronicleSessionIdAtom);

  if (!isRemoteSyncAsyncEnabled || !liveOffline || !sessionId) return false;
  return sessionId === liveOffline.sessionId || sessionId === activeChronicleSessionId;
}
